import { useEffect, useState } from "react";
import { Collapse, Grid, Typography } from "@mui/material";
import { calculateTotals, getCartOptions } from "@/utils/cart";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/utils/navigation";

import {
  CartList,
  CartNotification,
  CartSummary,
  ShoppingCartLinks,
} from "../components";
import Section from "@/components/Section";
import { fetchCartById } from "@/api/fetchCartById.ts";
import { useGetProductsQuery } from "@/redux-store/api/marketingApi.ts";
import { useAppDispatch, useAppSelector } from "@/redux-store/hooks.ts";
import { setCartData, setCartId } from "@/redux-store/cart/cartSlice.ts";

const ShoppingCart = () => {
  const { data } = useGetProductsQuery({});
  const [removeNotification, setRemoveNotification] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { products, totalItems, totalAmount, hasPlan, hasDevice, cartId } =
    useAppSelector((state) => state.cart);
  const sessionId = useAppSelector((state) => state.cart.sessionId);

  useEffect(() => {
    const getCheckoutData = async (id: string) => {
      try {
        const checkoutData = await fetchCartById(id);
        dispatch(setCartId(checkoutData?.id || ""));

        if (data) {
          const updatedProducts = checkoutData?.items?.map((item) => {
            const productData = data.find(
              (product) => product.id === item.idProduct
            );
            return {
              ...item,
              name: productData?.name || "No Name",
              image: productData?.image || "No Image",
              realPrice: productData?.price || 0,
            };
          }) || [];

          // Calculate totals
          const { totalQuantity, totalAmount } =
            calculateTotals(updatedProducts);
          const { hasPlan, hasDevice } = getCartOptions(updatedProducts);

          dispatch(
            setCartData({
              products: updatedProducts,
              totalItems: totalQuantity,
              totalAmount: totalAmount,
              hasPlan,
              hasDevice,
            })
          );
        }
      } catch (error: any) {
        console.error("Error fetching checkout:", error);
        if (error.response?.status === 404) {
          navigate(ROUTES.home);
        }
      }
    };

    if (data) {
      getCheckoutData(sessionId);
    }
  }, [data, sessionId, dispatch, navigate]);
  return (
    <Section pt={{ xs: 0, lg: 5.25 }} pb={{ xs: 6.25, md: 10, lg: 15.25 }}>
      <Typography
        variant="h1"
        fontSize={{ xs: "2rem", md: "3rem", lg: "3.125rem" }}
        mb={{ xs: 0.5, md: 0 }}
      >
        Shopping Cart
      </Typography>

      {products && products.length > 0 && (
        <>
          <Typography
            variant="body1"
            color="text.secondary"
            mb={{ xs: 2, md: 3.75 }}
            fontSize={{ xs: "0.875rem", md: "1rem", lg: "1.25rem" }}
            fontWeight={{ xs: 500, md: 600 }}
          >
            ({totalItems} items)
          </Typography>

          {/* Show notification if cart is not complete */}
          <Collapse in={!removeNotification && (!hasPlan || !hasDevice)}>
            <CartNotification
              isCartPage={true}
              hasDevice={hasDevice}
              hasPlan={hasPlan}
              removeNotification={() => {
                setRemoveNotification(true);
              }}
            />
          </Collapse>

          <Grid container spacing={{ xs: 7.5, lg: 12, xl: 19 }}>
            {/* Product list */}
            <Grid item xs={12} lg={7}>
              <CartList products={products} isCartPage={true} cartId={cartId} />
              <ShoppingCartLinks />
            </Grid>

            {/* Cart summary */}
            <Grid item xs={12} lg={5}>
              <CartSummary totalAmount={totalAmount} />
            </Grid>
          </Grid>
        </>
      )}
    </Section>
  );
};

export default ShoppingCart;
